import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FiTrash2, FiPlus, FiEdit2 } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import AdminContainer from "../components/AdminContainer";
import AdminTitle from "../components/utils/AdminTitle";
import AdminToast from "../components/utils/AdminToast";
import bundlesService from "../api/services/bundles";
import CustomSelect from "../components/utils/CustomSelect";
import { selectProducts } from "../store/slices/products/slice";
import { getProducts } from "../store/slices/products/asyncThunks";
import { FRONT_URL } from "../utils/constants";

const BundlePage = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);

  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productAId, setProductAId] = useState("");
  const [productBId, setProductBId] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteBundle, setDeleteBundle] = useState(null);
  const [toast, setToast] = useState(null);

  const [editBundle, setEditBundle] = useState(null);
  const [editAId, setEditAId] = useState("");
  const [editBId, setEditBId] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const openEdit = (b) => {
    setEditBundle(b);
    setEditAId(String(b.productAId ?? b.productA?.id ?? ""));
    setEditBId(String(b.productBId ?? b.productB?.id ?? ""));
    setEditAmount(String(b.discountAmount ?? ""));
  };

  const handleUpdateBundle = async () => {
    if (editLoading) return;
    if (!editAId || !editBId) {
      setToast({ type: "error", message: "Izaberi oba proizvoda." });
      return;
    }
    if (editAId === editBId) {
      setToast({ type: "error", message: "Izaberi dva različita proizvoda." });
      return;
    }
    const amount = Number(editAmount);
    if (!amount || amount <= 0) {
      setToast({ type: "error", message: "Iznos popusta mora biti veći od 0." });
      return;
    }
    setEditLoading(true);
    try {
      const res = await bundlesService.updateBundle(editBundle.id, {
        productAId: Number(editAId),
        productBId: Number(editBId),
        discountAmount: amount,
      });
      setBundles((prev) =>
        prev.map((b) => (b.id === editBundle.id ? res.data : b))
      );
      setEditBundle(null);
      setToast({ type: "success", message: "Bundle je izmenjen." });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setEditLoading(false);
    }
  };

  const productList = products?.data || [];

  const loadBundles = async () => {
    try {
      const res = await bundlesService.getBundles();
      setBundles(res.data || []);
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!productList.length) {
      dispatch(getProducts());
    }
    loadBundles();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async () => {
    if (!productAId || !productBId) {
      setToast({ type: "error", message: "Izaberi oba proizvoda." });
      return;
    }
    if (productAId === productBId) {
      setToast({ type: "error", message: "Izaberi dva različita proizvoda." });
      return;
    }
    const amount = Number(discountAmount);
    if (!amount || amount <= 0) {
      setToast({
        type: "error",
        message: "Iznos popusta mora biti veći od 0.",
      });
      return;
    }

    setCreating(true);
    try {
      const res = await bundlesService.createBundle({
        productAId: Number(productAId),
        productBId: Number(productBId),
        discountAmount: amount,
      });
      setBundles((prev) => [res.data, ...prev]);
      setProductAId("");
      setProductBId("");
      setDiscountAmount("");
      setToast({ type: "success", message: "Bundle je kreiran." });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await bundlesService.deleteBundle(deleteBundle.id);
      setBundles((prev) => prev.filter((b) => b.id !== deleteBundle.id));
      setDeleteBundle(null);
      setToast({ type: "success", message: "Bundle je obrisan." });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  };

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <AdminTitle kanji="組">Bundlovi</AdminTitle>

      <CreateBox>
        <FieldRow>
          <Field>
            <Label>Prvi proizvod</Label>
            <CustomSelect
              value={productAId}
              onChange={setProductAId}
              placeholder="Izaberi proizvod…"
              options={productList.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </Field>

          <PlusMark>+</PlusMark>

          <Field>
            <Label>Drugi proizvod</Label>
            <CustomSelect
              value={productBId}
              onChange={setProductBId}
              placeholder="Izaberi proizvod…"
              options={productList.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </Field>

          <Field $small>
            <Label>Popust (RSD)</Label>
            <Input
              type="number"
              placeholder="npr. 500"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
            />
          </Field>

          <ButtonAdd
            $disabled={creating || !productAId || !productBId || !discountAmount}
            onClick={creating ? undefined : handleCreate}
          >
            {creating ? (
              <l-line-spinner size="14" stroke="2" speed="1" color="#fe5f55" />
            ) : (
              <FiPlus size={16} />
            )}
            Kreiraj
          </ButtonAdd>
        </FieldRow>

        <Hint>
          Popust se skida sa porudžbine kada kupac ima <b>oba proizvoda</b> u
          korpi.
        </Hint>
      </CreateBox>

      <List>
        {loading ? (
          <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
        ) : bundles.length === 0 ? (
          <Empty>Još nema nijednog bundla.</Empty>
        ) : (
          bundles.map((b) => (
            <BundleCard key={b.id}>
              <Pair>
                <PairItem>
                  {b.productA?.thumbnailImage && (
                    <img
                      src={`${FRONT_URL}/thumbnails/${b.productA.thumbnailImage}`}
                      alt=""
                    />
                  )}
                  <span>{b.productA?.name || "—"}</span>
                </PairItem>
                <Plus>+</Plus>
                <PairItem>
                  {b.productB?.thumbnailImage && (
                    <img
                      src={`${FRONT_URL}/thumbnails/${b.productB.thumbnailImage}`}
                      alt=""
                    />
                  )}
                  <span>{b.productB?.name || "—"}</span>
                </PairItem>
              </Pair>

              <Right>
                <Discount>
                  -{Number(b.discountAmount).toLocaleString()} RSD
                </Discount>
                <IconBtn title="Izmeni bundle" onClick={() => openEdit(b)}>
                  <FiEdit2 size={15} />
                </IconBtn>
                <IconBtn
                  title="Obriši bundle"
                  onClick={() => setDeleteBundle(b)}
                >
                  <FiTrash2 size={15} />
                </IconBtn>
              </Right>
            </BundleCard>
          ))
        )}
      </List>

      <AnimatePresence>
        {editBundle && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !editLoading && setEditBundle(null)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              style={{ textAlign: "left", maxWidth: "52rem" }}
            >
              <CloseBtn onClick={() => !editLoading && setEditBundle(null)}>
                <FaTimes />
              </CloseBtn>
              <h3 style={{ textAlign: "center" }}>Izmeni bundle</h3>
              <EditField>
                <Label>Prvi proizvod</Label>
                <CustomSelect
                  value={editAId}
                  onChange={setEditAId}
                  placeholder="Izaberi proizvod…"
                  options={productList.map((pr) => ({
                    value: pr.id,
                    label: pr.name,
                  }))}
                />
              </EditField>
              <EditField>
                <Label>Drugi proizvod</Label>
                <CustomSelect
                  value={editBId}
                  onChange={setEditBId}
                  placeholder="Izaberi proizvod…"
                  options={productList.map((pr) => ({
                    value: pr.id,
                    label: pr.name,
                  }))}
                />
              </EditField>
              <EditField>
                <Label>Popust (RSD)</Label>
                <Input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="npr. 500"
                  style={{ width: "100%" }}
                />
              </EditField>
              <ModalActions>
                <Cancel onClick={() => !editLoading && setEditBundle(null)}>
                  Odustani
                </Cancel>
                <Confirm $disabled={editLoading} onClick={handleUpdateBundle}>
                  {editLoading ? "Čuvanje…" : "Sačuvaj"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteBundle && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteBundle(null)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setDeleteBundle(null)}>
                <FaTimes />
              </CloseBtn>
              <h3>Obriši bundle</h3>
              <p>
                Sigurno brišeš bundle{" "}
                <b>
                  {deleteBundle.productA?.name} + {deleteBundle.productB?.name}
                </b>
                ?
              </p>
              <ModalActions>
                <Cancel onClick={() => setDeleteBundle(null)}>Odustani</Cancel>
                <Confirm onClick={handleDelete}>Obriši</Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default BundlePage;

const CreateBox = styled.div`
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  padding: 1.8rem;
  width: 100%;
  max-width: 96rem;
  margin-bottom: 1.6rem;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: ${(props) => (props.$small ? "0 0 14rem" : "1")};
  min-width: ${(props) => (props.$small ? "14rem" : "20rem")};
`;

const PlusMark = styled.div`
  font-family: "Anton", sans-serif;
  font-size: 1.8rem;
  color: var(--red, #fe5f55);
  padding-bottom: 1.1rem;

  @media (max-width: 640px) {
    display: none;
  }
`;

const Label = styled.label`
  font-family: "Poppins-Medium";
  font-size: 1.05rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
`;

const Input = styled.input`
  padding: 0 1.2rem;
  height: 4.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  border-radius: 0.4rem;
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const ButtonAdd = styled.button`
  height: 4.4rem;
  padding: 0 2rem;
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  border-radius: 0.4rem;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  transition: background 0.25s var(--ease, ease);

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const Hint = styled.p`
  font-family: "Poppins-Regular";
  font-size: 1.2rem;
  color: var(--muted2, #5c5a63);
  margin-top: 1.2rem;

  b {
    font-family: "Poppins-SemiBold";
    color: var(--muted, #8e8c94);
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 96rem;
`;

const Empty = styled.div`
  font-family: "Poppins-Medium";
  font-size: 1.3rem;
  color: var(--muted, #8e8c94);
  padding: 2rem 0;
`;

const BundleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  padding: 1.2rem 1.6rem;
  flex-wrap: wrap;
  transition: border-color 0.25s;

  &:hover {
    border-color: var(--line2, rgba(255, 255, 255, 0.14));
  }
`;

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-wrap: wrap;
`;

const PairItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  img {
    width: 3.8rem;
    height: 4.6rem;
    object-fit: cover;
    border-radius: 0.3rem;
    border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  }

  span {
    font-family: "Poppins-SemiBold";
    font-size: 1.35rem;
    color: var(--text, #f4f2ee);
  }
`;

const Plus = styled.span`
  font-family: "Anton", sans-serif;
  font-size: 1.6rem;
  color: var(--red, #fe5f55);
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
`;

const Discount = styled.span`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  letter-spacing: 0.04em;
  color: var(--red, #fe5f55);
  background: rgba(254, 95, 85, 0.1);
  border: 1px solid rgba(254, 95, 85, 0.3);
  border-radius: 10rem;
  padding: 0.5rem 1.4rem;
`;

const IconBtn = styled.button`
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;

const EditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.4rem;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 6, 8, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: #fff;
  border-radius: 50%;
  font-size: 1.3rem;
  width: 3.2rem;
  height: 3.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s;

  &:hover {
    background: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const ConfirmBox = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 3rem 2.4rem;
  border-radius: 0.6rem;
  max-width: 46rem;
  width: 100%;
  color: var(--text, #f4f2ee);
  position: relative;
  text-align: center;

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 2rem;
    margin-bottom: 1.2rem;
  }

  p {
    font-family: "Poppins-Regular";
    font-size: 1.35rem;
    line-height: 1.6;
    margin-bottom: 2.2rem;
    color: var(--muted, #8e8c94);

    b {
      font-family: "Poppins-SemiBold";
      color: var(--red, #fe5f55);
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Cancel = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: transparent;
  color: var(--muted, #8e8c94);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--text, #f4f2ee);
    border-color: var(--text, #f4f2ee);
  }
`;

const Confirm = styled.button`
  padding: 1.1rem 2.2rem;
  border-radius: 0.4rem;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: background 0.25s;

  &:hover {
    background: var(--red-deep, #e04a40);
  }
`;
